import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faComment, // ✅ Added for Test SMS icon
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper } from "../../helpers/permission.js";

const TemplateSmsList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  const [filterLoading, setFilterLoading] = useState(false);
  const [senderList, setSenderList] = useState([]);

  // Search filter states
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [messageFilter, setMessageFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  useEffect(() => {
    fetchSenderList();
    fetchTemplates();
  }, []);

  const fetchSenderList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}settings/emailconfig/dropdown`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const senderOptions = response.data?.data?.sms?.map((item) => ({
        value: Number(item.value),
        label: item.label,
        sender: item.sender, // include sender here
      }));

      setSenderList(senderOptions || []);
    } catch (err) {
      console.error("Error fetching sender list", err);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}settings/template/sms/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setTemplates(response.data.data);
      setTotal(response.data.data.length);
      setPageCount(Math.ceil(response.data.data.length / limit));
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Added: Test SMS API integration
  const handleTestSms = async (templateId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}settings/template/sms/test-sms`,
        {},
        {
          params: { id: templateId },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      Swal.fire({
        title: "Success!",
        text: response.data?.message || "Test SMS triggered successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error sending Test SMS:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to send Test SMS. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  // ✅ End of added function

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const name = template.templatename || "";
      const message = template.message || "";

      if (
        templateNameFilter &&
        !name.toLowerCase().includes(templateNameFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        messageFilter &&
        !message.toLowerCase().includes(messageFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [templates, templateNameFilter, messageFilter]);

  useEffect(() => {
    if (!templates.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [templateNameFilter, messageFilter, templates]);


  useEffect(() => {
    setTotal(filteredTemplates.length);
    setPageCount(Math.ceil(filteredTemplates.length / limit));
    setPage(1);
  }, [filteredTemplates, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedTemplates = filteredTemplates.slice(start - 1, end);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_URL}settings/template/sms/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          Swal.fire("Deleted!", "The template has been deleted.", "success");
          fetchTemplates();
        } catch (error) {
          Swal.fire(
            "Error!",
            "There was an error deleting the template.",
            "error"
          );
        }
      } else {
        Swal.fire("Cancelled", "Your record is safe", "error");
      }
    });
  };

  const getSenderLabel = (senderType) => {
    const sender = senderList.find((s) => s.value === senderType);
    return sender
      ? `${sender.label} (${sender.sender || "-"})`
      : senderType || "-";
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTemplateNameFilter("");
      setMessageFilter("");
    };
    window.addEventListener('clearTemplatesmsFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearTemplatesmsFilterEvent', handleClearFilters);
    };
  }, []);


  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Template SMS</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="settings_sms" action="add">
              <Link
                className="btn btn-default btn-sm me-2"
                to={"/Settings/TemplateSms/add"}
              >
                <FontAwesomeIcon icon={faPlus} /> Add SMS Template
              </Link>
            </PermissionWrapper>

            {templates.length > 0 && (
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {loading ? (
            <span></span>
          ) : (
            <>
              {/* Add CSS for sticky headers */}
              <style>
                {`
                  .sticky-sms-table thead tr:first-child th {
                    position: sticky;
                    top: 0;
                    z-index: 20;
                    background: #f8f9fa;
                  }
                  
                  .sticky-sms-table thead tr:nth-child(2) th {
                    position: sticky;
                    top: 30px; /* Adjust based on your first header row height */
                    z-index: 19;
                    background: white;
                  }
                  
                  .sticky-sms-container {
                    max-height: calc(100vh - 165px);
                    overflow: auto;
                  }
                  
                  .sticky-sms-table th {
                    vertical-align: top;
                  }
                `}
              </style>

              <div className="sticky-sms-container">
                {loading || templates.length > 0 ? (
                  <table className="table m-b-0 sticky-sms-table">
                    <thead>
                      {/* First Header Row - Column Titles */}
                      <tr>
                        <th style={{ textAlign: "center" }}>#</th>
                        <th>Template Name</th>
                        <th>Message</th>
                        <th>Sender Type</th>
                        <th style={{ width: "120px", textAlign: "center" }}>
                          Test SMS {/* ✅ Added header */}
                        </th>
                        <th style={{ width: "80px" }}>Action</th>
                      </tr>

                      {/* Second Header Row - Search Filters */}
                      <tr>
                        <th></th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search name..."
                            value={templateNameFilter}
                            onChange={(e) =>
                              setTemplateNameFilter(e.target.value)
                            }
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search message..."
                            value={messageFilter}
                            onChange={(e) => setMessageFilter(e.target.value)}
                          />
                        </th>
                        <th></th>
                        <th></th>
                        <th>
                          <button
                            className="btn btn-default btn-sm"
                            onClick={() => {
                              setTemplateNameFilter("");
                              setMessageFilter("");
                            }}
                          >
                            Clear
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading || filterLoading ? (
                        Array.from({ length: 10 }).map((_, rowIndex) => (
                          <tr key={rowIndex} className="skeleton-row">
                            {Array.from({ length: 6 }).map((_, colIndex) => (
                              <td key={colIndex}>
                                <div className="skeleton-box"></div>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : paginatedTemplates.length > 0 ? (
                        paginatedTemplates.map((post, index) => (
                          <tr key={post.id}>
                            <td style={{ textAlign: "center" }}>
                              {(page - 1) * limit + index + 1}
                            </td>
                            <td>{post.templatename}</td>
                            <td
                              style={{
                                maxWidth: "900px",
                                wordWrap: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {post.message}
                            </td>
                            <td>{getSenderLabel(post.sender_type)}</td>
                            <td className="text-center">
                              <button
                                onClick={() => handleTestSms(post.id)}
                                className="btn btn-default btn-xs"
                                title="Send Test SMS"
                              >
                                <FontAwesomeIcon icon={faComment} />
                              </button>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <PermissionWrapper module="settings_sms" action="edit">
                                  <Link
                                    className="btn btn-warning btn-xs"
                                    to={`/Settings/TemplateSms/edit/${post.id}`}
                                    title="Edit Template SMS"
                                  >
                                    <FontAwesomeIcon icon={faPencil} />
                                  </Link>
                                </PermissionWrapper>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No templates match your search criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>

                  </table>
                ) : (
                  <div className="alert alert-warning m-3">
                    No templates found.{" "}
                    <Link to="/Settings/TemplateSms/add">Add SMS Template</Link>.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateSmsList;